import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";
import { Loader2, Radio, Monitor, Smartphone } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-950">
      {/* Brand panel */}
      <div className="lg:w-1/2 relative overflow-hidden flex flex-col justify-between p-8 lg:p-14 bg-slate-900 border-b lg:border-b-0 lg:border-r border-slate-800">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1504904126298-3fde501c9b31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
        <div className="relative z-10 flex items-center gap-3 mb-10 lg:mb-16">
          <img src="/logo.webp" alt="GCAVCOM" className="h-10 w-auto" />
        </div>
        <div className="relative z-10 max-w-md animate-fade-up">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            La App di GCAVCOM per gestire<br />
            <span className="text-cyan-400">date, servizi e disponibilità dei tecnici.</span>
          </h1>
          <div className="flex gap-5 mt-6 text-slate-400 text-sm">
            <span className="flex items-center gap-2"><Monitor className="w-4 h-4 text-cyan-400" /> Manager su PC</span>
            <span className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-cyan-400" /> Tecnici su mobile</span>
          </div>
        </div>
        <div className="relative z-10" />
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md animate-fade-up">
          <div className="mb-6">
            <h2 className="font-display text-2xl font-bold text-white">Accedi</h2>
            <p className="text-sm text-slate-400 mt-1">Inserisci le credenziali fornite dal manager.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required placeholder="nome@email.com"
                className="bg-slate-800/60 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" data-testid="password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                className="bg-slate-800/60 border-slate-700" />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}

            <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Accedi
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
